import React, { useState, useEffect, useCallback } from 'react';
import { BackofficeLayout } from '../../layouts/BackofficeLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, ShieldCheck, ShieldAlert, FileText,
  Users, Download, Trash2, Mail, CheckCircle, XCircle,
  Clock, AlertTriangle, Rocket, Globe,
} from 'lucide-react';

function timeAgo(iso) {
  if (!iso) return '';
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

function PassFail({ passed }) {
  return passed
    ? <CheckCircle className="w-4 h-4 text-emerald-500" />
    : <XCircle className="w-4 h-4 text-red-400" />;
}

export default function CompliancePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/legal/admin/compliance');
      setData(res.data);
    } catch {
      toast.error('Failed to load compliance data');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const completeRequest = async (id) => {
    setCompleting(id);
    try {
      await api.post(`/legal/admin/data-request/${id}/complete`);
      toast.success('Request marked as completed');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
    setCompleting(null);
  };

  if (loading) {
    return (
      <BackofficeLayout>
        <div className="flex justify-center py-32"><Loader2 className="w-5 h-5 animate-spin text-slate-600" /></div>
      </BackofficeLayout>
    );
  }

  const v = data?.legal_versions || {};
  const c = data?.consent || {};
  const dr = data?.data_requests || {};
  const em = data?.email_compliance || {};
  const dp = data?.deploy_readiness || {};

  return (
    <BackofficeLayout>
      <div className="space-y-5 max-w-5xl mx-auto" data-testid="compliance-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-black text-white tracking-wider" style={{ fontFamily: 'Barlow Condensed' }}>
              COMPLIANCE & GOVERNANCE
            </h1>
            <p className="text-[10px] text-slate-500 mt-0.5">Legal governance, consent tracking, email compliance</p>
          </div>
          <Button size="sm" variant="ghost" onClick={() => { setLoading(true); load(); }} className="text-slate-400 hover:text-white text-[10px] h-7" data-testid="refresh-compliance">
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
        </div>

        {/* Deploy Readiness Banner */}
        {!dp.ready && (
          <div className="flex items-start gap-3 p-4 bg-amber-950/15 border border-amber-500/25 rounded-sm" data-testid="deploy-warning-banner">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-300">Legal governance incomplete — deployment not recommended.</p>
              <p className="text-[10px] text-amber-400/70 mt-1">
                {dp.checks?.filter(c => !c.passed).map(c => c.label).join(' / ')}
              </p>
            </div>
          </div>
        )}
        {dp.ready && (
          <div className="flex items-center gap-3 p-3 bg-emerald-950/15 border border-emerald-500/25 rounded-sm" data-testid="deploy-ready-banner">
            <Rocket className="w-4 h-4 text-emerald-400" />
            <p className="text-xs text-emerald-300">All legal governance checks passed. Deployment ready.</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

          {/* Legal Versions */}
          <Card className="bg-slate-900 border-slate-800" data-testid="legal-versions-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-[11px] uppercase tracking-[0.15em] text-slate-400 flex items-center gap-2">
                <FileText className="w-3.5 h-3.5" /> Legal Versions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Terms', version: v.terms_version },
                  { label: 'Privacy', version: v.privacy_version },
                  { label: 'Cookies', version: v.cookies_version },
                ].map(item => (
                  <div key={item.label} className="text-center py-3 bg-slate-800/60 rounded-sm">
                    <span className="text-lg font-black text-white" style={{ fontFamily: 'Barlow Condensed' }}>v{item.version}</span>
                    <p className="text-[9px] text-slate-500 uppercase mt-1">{item.label}</p>
                  </div>
                ))}
              </div>
              <p className="text-[9px] text-slate-600 text-center">Effective: {v.effective_date}</p>
            </CardContent>
          </Card>

          {/* Consent Tracking */}
          <Card className="bg-slate-900 border-slate-800" data-testid="consent-tracking-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-[11px] uppercase tracking-[0.15em] text-slate-400 flex items-center gap-2">
                <Users className="w-3.5 h-3.5" /> Consent Tracking
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center py-3 bg-slate-800/60 rounded-sm">
                  <span className="text-xl font-black text-emerald-400" style={{ fontFamily: 'Barlow Condensed' }}>{c.users_current}</span>
                  <p className="text-[9px] text-slate-500 uppercase mt-1">Current Consent</p>
                </div>
                <div className="text-center py-3 bg-slate-800/60 rounded-sm">
                  <span className={`text-xl font-black ${c.users_needing_reconsent > 0 ? 'text-amber-400' : 'text-slate-400'}`} style={{ fontFamily: 'Barlow Condensed' }}>{c.users_needing_reconsent}</span>
                  <p className="text-[9px] text-slate-500 uppercase mt-1">Need Re-consent</p>
                </div>
              </div>
              <div className="flex items-center justify-between mt-3 px-1 text-[9px] text-slate-500">
                <span>Total consents: {c.total_consents}</span>
                <span>Registration: {c.registration_consents}</span>
                <span>Users: {c.total_users}</span>
              </div>
            </CardContent>
          </Card>

          {/* Email Domain Verification */}
          <Card className="bg-slate-900 border-slate-800" data-testid="email-compliance-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-[11px] uppercase tracking-[0.15em] text-slate-400 flex items-center gap-2">
                <Mail className="w-3.5 h-3.5" /> Email Domain Verification
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!em.configured ? (
                <div className="text-center py-4">
                  <XCircle className="w-5 h-5 text-slate-600 mx-auto mb-2" />
                  <p className="text-[10px] text-slate-500">RESEND_FROM_EMAIL not configured</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 px-1 mb-2">
                    <Globe className="w-3 h-3 text-slate-500" />
                    <span className="text-xs text-slate-300 font-mono">{em.domain}</span>
                    <Badge className={em.verified ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30 text-[8px] px-1.5 ml-auto' : 'bg-red-500/10 text-red-400 border-red-500/30 text-[8px] px-1.5 ml-auto'}>
                      {em.verified ? 'Verified' : 'Not Verified'}
                    </Badge>
                  </div>
                  {['spf', 'dkim', 'dmarc'].map(check => (
                    <div key={check} className="flex items-center justify-between px-3 py-2 bg-slate-800/40 rounded-sm">
                      <span className="text-xs text-slate-400 uppercase font-medium">{check}</span>
                      <PassFail passed={em[check]} />
                    </div>
                  ))}
                  {em.warning && (
                    <p className="text-[9px] text-amber-400 mt-2 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> {em.warning}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Deploy Readiness Checks */}
          <Card className="bg-slate-900 border-slate-800" data-testid="deploy-readiness-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-[11px] uppercase tracking-[0.15em] text-slate-400 flex items-center gap-2">
                <Rocket className="w-3.5 h-3.5" /> Deploy Readiness
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1.5">
                {(dp.checks || []).map(check => (
                  <div key={check.key} className="flex items-center justify-between px-3 py-2 bg-slate-800/40 rounded-sm">
                    <span className="text-xs text-slate-400">{check.label}</span>
                    <PassFail passed={check.passed} />
                  </div>
                ))}
              </div>
              {dp.checked_at && (
                <p className="text-[9px] text-slate-600 text-center mt-2">Checked {timeAgo(dp.checked_at)}</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Data Subject Requests */}
        <Card className="bg-slate-900 border-slate-800" data-testid="data-requests-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[11px] uppercase tracking-[0.15em] text-slate-400 flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5" /> Data Subject Requests
              </CardTitle>
              <div className="flex gap-2">
                <Badge className="bg-slate-800 text-slate-400 border-slate-700 text-[8px] px-1.5">
                  <Download className="w-2.5 h-2.5 mr-0.5" /> Export: {dr.export_pending} pending / {dr.export_completed} done
                </Badge>
                <Badge className="bg-slate-800 text-slate-400 border-slate-700 text-[8px] px-1.5">
                  <Trash2 className="w-2.5 h-2.5 mr-0.5" /> Delete: {dr.deletion_pending} pending / {dr.deletion_completed} done
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {(dr.recent || []).length === 0 ? (
              <div className="text-center py-6">
                <CheckCircle className="w-5 h-5 text-emerald-500/40 mx-auto mb-2" />
                <p className="text-[10px] text-slate-500">No data requests</p>
              </div>
            ) : (
              <div className="space-y-1.5" data-testid="data-requests-list">
                {dr.recent.map(r => (
                  <div key={r.id} className="flex items-center justify-between px-3 py-2.5 bg-slate-800/40 rounded-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      {r.type === 'export' ? <Download className="w-3.5 h-3.5 text-blue-400 shrink-0" /> : <Trash2 className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                      <span className="text-xs text-slate-300 truncate">{r.email}</span>
                      <Badge className="bg-slate-700 text-slate-400 text-[8px] px-1">{r.type}</Badge>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[9px] text-slate-600">{timeAgo(r.requested_at)}</span>
                      {r.status === 'pending' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => completeRequest(r.id)}
                          disabled={completing === r.id}
                          className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-[9px] h-6 px-2"
                          data-testid={`complete-request-${r.id}`}
                        >
                          {completing === r.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : 'Complete'}
                        </Button>
                      ) : (
                        <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30 text-[8px] px-1.5">
                          <CheckCircle className="w-2.5 h-2.5 mr-0.5" /> Done
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </BackofficeLayout>
  );
}
